<template>
  <div class="edit-view display-in-center view" v-loading.fullscreen="loading || !isReadyToScan">
    <div class="step-label">
      {{ $t('barcode_recycle') }}
    </div>
    <div class="barcode-scanner">
      <div id="scan-area">
        <div class="scan-selection-camera" @click="openDialog" v-show="cameras.length > 0">
          <IconSwitch />
        </div>
        <div id="video-area">
          <video playsinline autoplay id="video"></video>
        </div>
      </div>

      <BarcodeSymbologiesSelection
        class="absolute bottom-2 z-50 left-2 flex w-52"
        @update:onSymbologiesChange="setBarcodeSymbologies"
        v-model:symbologies="symbologies"
      />

      <ScanArea />
    </div>
    <div class="barcode-number-block">
      <span>
        {{ $t('Number of Scanned Barcodes') }}
      </span>
      <span class="label-box" @click="onCheckScannedBarcode">
        {{ scannedBarcodeList.length }}
      </span>
    </div>
    <el-button
      circle
      :disabled="scannedBarcodeList.length === 0"
      type="primary"
      class="submit-button"
      @click="submit()"
    >
      {{ $t('OK') }}
    </el-button>
  </div>
  <el-dialog
    :lock-scroll="true"
    center
    v-model="dialogVisible"
    width="90%"
    top="0"
    :show-close="false"
    :destroy-on-close="false"
    :title="checkScannedBarcode ? $t('Scanned Barcodes') : ''"
  >
    <template v-if="checkScannedBarcode">
      <p v-if="scannedBarcodeList.length === 0">
        {{ $t('no barcodes') }}
      </p>
      <BarcodeLabelList v-else :barcodes="scannedBarcodeList || []" />
    </template>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="onCloseDialog">
          {{ $t('OK') }}
          <KeyenceHTHardKeyButton
            v-if="isKeyenceEnterpriseBrowser"
            :hardkey="KeyenceHardKey.ENTER"
            :function="onCloseDialog"
            :visible="dialogVisible"
            :newLayer="true"
          />
        </el-button>
      </span>
    </template>
  </el-dialog>
  <SelectionCameraPopup v-model:selectedCamera="selectedCamera" :cameras="cameras" v-model:isShow="isOpenDialog" />
</template>

<script lang="ts">
import BarcodeLabelList from '@/components/BarcodeLabelList.vue'
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import MultipleBarcodeScanMixin from '@/components/mixins/MultipleBarcodeScanMixin.vue'
import ScanArea from '@/components/svg/ScanArea.vue'
import { RECYCLE_MULTIPLE_BARCODE, SAVE_SCANNED_BARCODES } from '@/store/actions'
import { recycleMultipleBarcode } from '@/utils/api'
import { Options, mixins } from 'vue-class-component'
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import { IBarcode } from '@/utils/types/barcodeTypes'
import { IBarcodeDefinitionType } from '@/utils/types/fieldDefinition'
import { IProject } from '@/utils/types/projectTypes'
@Options({
  components: {
    ScanArea,
    BarcodeLabelList,
    BarcodeSymbologiesSelection,
    IconSwitch,
    SelectionCameraPopup,
    KeyenceHTHardKeyButton,
  },
  name: 'BulkRecycle',
})
export default class BulkRecycle extends mixins(MultipleBarcodeScanMixin) {
  KeyenceHardKey = KeyenceHardKey

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  loading = false
  dialogVisible = false
  checkScannedBarcode = false
  extParams = {} as Record<string, unknown>
  isReadyToScan = true
  messageThrow = ''

  async beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    await this.onLeaveGuard(to, from, next)
  }

  created() {
    this.isForcedHideMultipleScan = true
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.dialogVisible = true
  }

  onCloseDialog() {
    this.dialogVisible = false
    this.checkScannedBarcode = false
  }

  barcodeTypeInfo(barcodeType: string): IBarcodeDefinitionType {
    return this.$store.state.project?.details?.barcodeTypes[barcodeType]
  }

  get barcode(): IBarcode {
    return this.$store.state.barcode?.barcode
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  isCurrentRecycleableTrackpoint(barcode: IBarcode): boolean {
    return (this.barcodeTypeInfo(barcode.barcodeType)?.barcodeRecyclableTrackPoints ?? []).includes(
      this.currentTrackPointKey
    )
  }

  isAllowRecycle(barcode: IBarcode): { message: string } {
    const error = { message: '' }
    if (!this.isCurrentRecycleableTrackpoint(barcode)) {
      error.message = 'cant_recycle_at_this_trackpoint'
    }

    if (this.childrenCount > 0) {
      if (!this.project.isRecycleChildren || this.hasParent(barcode)) {
        error.message = 'errors.3025'
      }
    }

    if (this.parentBarcodeId === barcode?.id) {
      error.message = 'barcode_pairing_by_itself_error'
    }

    if (!barcode?.barcodeType) {
      error.message = 'barcode not activated'
    }
    return error
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  hasParent(barcode: IBarcode): boolean {
    return barcode?.hasParent
  }

  get childrenCount() {
    return this.barcode?.childrenCount || 0
  }

  async validateBarcode(resultScanned: IBarcode) {
    const error = this.isAllowRecycle(resultScanned)
    if (error.message) {
      throw this.$t(error.message)
    }
    const data = {
      barcodeIds: [resultScanned?.id],
      isDryRun: true,
    }
    try {
      await recycleMultipleBarcode(data)
    } catch (error) {}
  }

  get parentBarcodeId() {
    return this.$store.state.barcode?.barcode?.id || ''
  }

  forward() {
    if (!this.barcodes || this.barcodes.length === 0) {
      return
    }
    const listBarcodeIds = [this.$route.params.barcodeId, ...this.barcodes.map((barcode: IBarcode) => barcode.id)]
    this.$store.dispatch(SAVE_SCANNED_BARCODES, this.barcodes)
    this.$router.push({
      name: 'scanned-barcode-list',
      params: {
        title: this.$t('recycle_read_result'),
        operation: RECYCLE_MULTIPLE_BARCODE,
        extParams: JSON.stringify(listBarcodeIds),
      },
    })
  }
}
</script>

<style lang="scss" scoped>
@import './src/assets/css/mixins.scss';

.edit-view.view {
  padding-left: 0;
  padding-right: 0;
}

.step-label {
  margin-bottom: 25px;
}

.barcode-scanner {
  height: 430px;
  background: rgb(32, 32, 32);
  position: relative;
}

.barcode-scanner video {
  width: 100%;
  height: 100%;
}

.scan-selection-camera {
  position: absolute;
  top: 0.5em;
  left: 0.5em;
}
</style>
